import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getComicsByCategory } from '@/mocks/comics';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const currentCategory = categories.find(c => c.slug === slug) || categories[0];
  const allComics = useMemo(() => getComicsByCategory(slug), [slug]);

  const sortedComics = useMemo(() => {
    const list = [...allComics];
    if (sortBy === 'rating') list.sort((a, b) => b.rating - a.rating);
    if (sortBy === 'views') list.sort((a, b) => parseInt(b.views) - parseInt(a.views));
    return list;
  }, [allComics, sortBy]);

  const totalPages = Math.ceil(sortedComics.length / ITEMS_PER_PAGE);
  const paginatedComics = sortedComics.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleCategoryChange = () => setPage(1);

  return (
    <>
      <Breadcrumb items={[
        { label: currentCategory.name === '全部' ? '漫画分类' : '漫画分类', href: '/category/all' },
        { label: currentCategory.name },
      ]} />

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
            {currentCategory.name}
          </h1>
          <p className="text-sm text-foreground-500 mt-1">共 {allComics.length} 部漫画</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <aside className="lg:w-48 flex-shrink-0">
            <div className="sticky top-20">
              <div className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
                {categories.map(cat => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={handleCategoryChange}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors cursor-pointer flex-shrink-0 ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-white font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>

          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {[
                  { key: 'latest', label: '最新' },
                  { key: 'rating', label: '评分' },
                  { key: 'views', label: '人气' },
                ].map(s => (
                  <button
                    key={s.key}
                    onClick={() => { setSortBy(s.key as typeof sortBy); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === s.key
                        ? 'bg-white text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {paginatedComics.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {paginatedComics.map(comic => (
                    <Link key={comic.id} to={`/comic/${comic.id}`} className="group cursor-pointer">
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-200 mb-3">
                        <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                        <div className="absolute top-2 left-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                            comic.status === '连载中' ? 'bg-primary-500 text-white' : 'bg-secondary-500 text-white'
                          }`}>
                            {comic.status}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">{comic.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-accent-500 font-medium whitespace-nowrap">
                          <i className="ri-star-fill w-3 h-3 inline-flex items-center justify-center"></i> {comic.rating}
                        </span>
                        <span className="text-xs text-foreground-400">{comic.views}</span>
                      </div>
                    </Link>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line w-5 h-5 flex items-center justify-center"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                          p === page
                            ? 'bg-primary-500 text-white font-medium'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300 w-10 h-10 flex items-center justify-center mx-auto"></i>
                <p className="mt-3 text-foreground-500">该分类暂无漫画，去看看其他分类吧</p>
                <Link to="/category/all" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
                  查看全部漫画
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `迷妹漫画 - ${currentCategory.name}`,
            "description": `迷妹漫画${currentCategory.name}分类，提供海量免费${currentCategory.name}漫画在线看。`,
            "url": `https://mimei-comic.com/category/${slug}`,
            "inLanguage": "zh-CN",
            "isPartOf": {
              "@type": "WebSite",
              "name": "迷妹漫画",
              "url": "https://mimei-comic.com",
            },
          }),
        }}
      />
    </>
  );
}